<script>
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { 
    faChartLine,
    faPercent,
    faWaveSquare,
    faArrowUp,
    faBullseye,
    faArrowDown
  } from '@fortawesome/pro-light-svg-icons';
  import { isCompactMode } from '$stores/uiStore';
  import { glucoseunit } from '$stores/unitsStore';
  import { convertGlucoseValue } from '$utils/unitUtils';

  export let readings = [];
  export let studyProperties = null;

  // Base ranges in mg/dL
  const defaultRanges = {
    veryHigh: 250,
    high: 180,
    targetLow: 70,
    veryLow: 54,
  };

  // Base ranges from study settings (fallback to defaults)
  $: baseRanges = {
    veryHigh: studyProperties?.glucoseParameters?.thresholds?.veryHigh ?? defaultRanges.veryHigh,
    high: studyProperties?.glucoseParameters?.thresholds?.high ?? defaultRanges.high,
    targetLow: studyProperties?.glucoseParameters?.thresholds?.low ?? defaultRanges.targetLow,
    veryLow: studyProperties?.glucoseParameters?.thresholds?.veryLow ?? defaultRanges.veryLow,
  };

  // Reactive ranges that update with unit changes
  $: ranges = {
    veryHigh: convertGlucoseValue(baseRanges.veryHigh, $glucoseunit) || 0,
    high: convertGlucoseValue(baseRanges.high, $glucoseunit) || 0,
    targetLow: convertGlucoseValue(baseRanges.targetLow, $glucoseunit) || 0,
    veryLow: convertGlucoseValue(baseRanges.veryLow, $glucoseunit) || 0,
  };

  // Declare variables before using them in reactive statement
  let averageGlucose = 0;
  let gmi = 0;
  let glucoseVariability = 0;
  let tar = 0;
  let tir = 0;
  let tbr = 0;

  // Calculate metrics
  $: {
    // Calculate Average Glucose
    if (readings && readings.length > 0) {
      const validReadings = readings.filter(r => r && r.value != null);
      const convertedReadings = validReadings.map(r => ({
        ...r,
        value: convertGlucoseValue(r.value, $glucoseunit) || 0
      }));
      
      averageGlucose = convertedReadings.length > 0 
        ? convertedReadings.reduce((sum, r) => sum + r.value, 0) / convertedReadings.length 
        : 0;

      // Calculate GMI (Glucose Management Indicator)
      // Convert average back to mg/dL for GMI calculation if needed 
      const avgForGMI = $glucoseunit === 'mmol/L' ? averageGlucose * 18 : averageGlucose;
      gmi = 3.31 + 0.02392 * avgForGMI;

      // Calculate Glucose Variability (CV)
      if (convertedReadings.length > 1) {
        const variance = convertedReadings.reduce((sum, r) => {
          return sum + Math.pow(r.value - averageGlucose, 2);
        }, 0) / (convertedReadings.length - 1); // Use sample standard deviation (n-1)
        const standardDeviation = Math.sqrt(variance);
        glucoseVariability = (standardDeviation / averageGlucose) * 100;
      } else {
        glucoseVariability = 0;
      }

      // Calculate Time in Ranges
      const counts = {
        veryLow: convertedReadings.filter((r) => r.value < ranges.veryLow).length,
        low: convertedReadings.filter((r) => r.value < ranges.targetLow && r.value >= ranges.veryLow).length,
        target: convertedReadings.filter((r) => r.value >= ranges.targetLow && r.value <= ranges.high).length,
        high: convertedReadings.filter((r) => r.value > ranges.high && r.value <= ranges.veryHigh).length,
        veryHigh: convertedReadings.filter((r) => r.value > ranges.veryHigh).length,
      };

      // Calculate percentages
      tar = ((counts.high + counts.veryHigh) / convertedReadings.length) * 100;
      tir = (counts.target / convertedReadings.length) * 100;
      tbr = ((counts.low + counts.veryLow) / convertedReadings.length) * 100;
    } else {
      averageGlucose = 0;
      gmi = 0;
      glucoseVariability = 0;
      tar = 0;
      tir = 0;
      tbr = 0;
    }
  }
</script>

<div class="grid grid-cols-6 gap-4 px-1 mt-3">
  <!-- Average Glucose Card -->
  <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 border-[#4BC0C0]">
    <div class="flex items-center gap-2 mb-2">
      <FontAwesomeIcon icon={faChartLine} class="text-xl text-[#4BC0C0]" />
      <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300">Average</h3>
    </div>
    <p class="text-lg font-bold text-gray-900 dark:text-white">
      {#if averageGlucose != null}
        {#if $glucoseunit === 'mmol/L'}
          {averageGlucose.toFixed(1)} {$glucoseunit}
        {:else}
          {averageGlucose.toFixed(0)} {$glucoseunit}
        {/if}
      {:else}
        -
      {/if}
    </p>
    {#if !$isCompactMode}
      <p class="text-xs text-gray-500 dark:text-gray-400">Mean glucose value</p>
    {/if}
  </div>

  <!-- GMI Card -->
  <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 border-[#FF6384]">
    <div class="flex items-center gap-2 mb-2">
      <FontAwesomeIcon icon={faPercent} class="text-xl text-[#FF6384]" />
      <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300">GMI</h3>
    </div>
    <p class="text-lg font-bold text-gray-900 dark:text-white">
      {#if gmi != null}
        {gmi.toFixed(1)}%
      {:else}
        -
      {/if}
    </p>
    {#if !$isCompactMode}
      <p class="text-xs text-gray-500 dark:text-gray-400">Glucose Management Indicator</p>
    {/if}
  </div>

  <!-- CV Card -->
  <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 border-[#0A96C8]">
    <div class="flex items-center gap-2 mb-2">
      <FontAwesomeIcon icon={faWaveSquare} class="text-xl text-[#0A96C8]" />
      <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300">CV</h3>
    </div>
    <p class="text-lg font-bold text-gray-900 dark:text-white">
      {#if glucoseVariability != null}
        {glucoseVariability.toFixed(1)}%
      {:else}
        -
      {/if}
    </p>
    {#if !$isCompactMode}
      <p class="text-xs text-gray-500 dark:text-gray-400">Coefficient of Variation</p>
    {/if}
  </div>

  <!-- TAR Card -->
  <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 border-[#FFCE56]">
    <div class="flex items-center gap-2 mb-2">
      <FontAwesomeIcon icon={faArrowUp} class="text-xl text-[#FFCE56]" />
      <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300">TAR</h3>
    </div>
    <p class="text-lg font-bold text-gray-900 dark:text-white">
      {#if tar != null}
        {tar.toFixed(1)}%
      {:else}
        -
      {/if}
    </p>
    {#if !$isCompactMode}
      <p class="text-xs text-gray-500 dark:text-gray-400">
        Time Above Range
        {#if $glucoseunit === 'mmol/L'}
          &gt;{ranges.high.toFixed(1)} {$glucoseunit}
        {:else}
          &gt;{ranges.high.toFixed(0)} {$glucoseunit}
        {/if}
      </p>
    {/if}
  </div>

  <!-- TIR Card -->
  <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 border-[#4BC0C0]">
    <div class="flex items-center gap-2 mb-2">
      <FontAwesomeIcon icon={faBullseye} class="text-xl text-[#4BC0C0]" />
      <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300">TIR</h3>
    </div>
    <p class="text-lg font-bold text-gray-900 dark:text-white">
      {#if tir != null}
        {tir.toFixed(1)}%
      {:else}
        -
      {/if}
    </p>
    {#if !$isCompactMode}
      <p class="text-xs text-gray-500 dark:text-gray-400">
        Time In Range 
        {#if $glucoseunit === 'mmol/L'}
          {ranges.targetLow.toFixed(1)}-{ranges.high.toFixed(1)} {$glucoseunit}
        {:else}
          {ranges.targetLow.toFixed(0)}-{ranges.high.toFixed(0)} {$glucoseunit}
        {/if}
      </p>
    {/if}
  </div>

  <!-- TBR Card -->
  <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 border-[#FF6384]">
    <div class="flex items-center gap-2 mb-2">
      <FontAwesomeIcon icon={faArrowDown} class="text-xl text-[#FF6384]" />
      <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300">TBR</h3>
    </div>
    <p class="text-lg font-bold text-gray-900 dark:text-white">
      {#if tbr != null}
        {tbr.toFixed(1)}%
      {:else}
        -
      {/if}
    </p>
    {#if !$isCompactMode}
      <p class="text-xs text-gray-500 dark:text-gray-400">
        Time Below Range
        {#if $glucoseunit === 'mmol/L'}
          &lt;{ranges.targetLow.toFixed(1)} {$glucoseunit}
        {:else}
          &lt;{ranges.targetLow.toFixed(0)} {$glucoseunit}
        {/if}
      </p>
    {/if}
  </div>
</div> 